import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock3,
  FolderKanban,
  GraduationCap,
  Layers3,
  MessageSquareText,
  PlayCircle,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  UserRound,
  Star,
  ChevronDown,
  Lock,
  Eye,
  Award,
  TrendingUp,
} from "lucide-react";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger.js";
import {
  courseFallbackImage,
  enrollInCourse,
  formatCurrency,
  getCourseDescription,
  getCourseImage,
  getCourseLearnList,
  getCoursePrice,
  getCourseRequirements,
  getCourseSummary,
  getCourseTitle,
  getMyCourses,
  getCourseDetails,
  listCourseReviews,
} from "../api/courses";
import "./DetailPages.css";

function splitLines(value) {
  return String(value || "")
    .split(/\r?\n|•/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function CourseDetails() {
  const { courseRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true); setError(""); setNotice("");
        const [details, courseReviews] = await Promise.all([
          getCourseDetails(courseRef),
          listCourseReviews(courseRef).catch(() => []),
        ]);
        if (!active) return;
        setCourse(details);
        setReviews(Array.isArray(courseReviews) ? courseReviews : []);
        setOpenSectionId(details?.sections?.[0]?.id || null);
        if (isLogged) {
          try {
            const myCourses = await getMyCourses();
            if (!active) return;
            const matched = myCourses.find(
              (item) => Number(item?.course?.id) === Number(details?.id)
            );
            setEnrollment(matched?.course ? matched : null);
          } catch (e) { logger.warn("My courses fetch failed", e); }
        } else { setEnrollment(null); }
      } catch (err) {
        logger.error("Course details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الكورس." : "Failed to load course details.");
      } finally { if (active) setLoading(false); }
    }
    load();
    return () => { active = false; };
  }, [courseRef, isAr, isLogged]);

  const title       = useMemo(() => getCourseTitle(course, isAr),        [course, isAr]);
  const summary     = useMemo(() => getCourseSummary(course, isAr),      [course, isAr]);
  const description = useMemo(() => getCourseDescription(course, isAr),  [course, isAr]);
  const learnPoints = useMemo(() => splitLines(getCourseLearnList(course, isAr)),    [course, isAr]);
  const requirements= useMemo(() => splitLines(getCourseRequirements(course, isAr)), [course, isAr]);
  const courseUrlRef = course?.slug || course?.id || courseRef;
  const courseImage  = useMemo(() => getCourseImage(course), [course]);

  const handleImageError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied === "true") return;
    e.currentTarget.dataset.fallbackApplied = "true";
    e.currentTarget.src = courseFallbackImage;
    e.currentTarget.classList.add("cd-image--contain");
    e.currentTarget.parentElement?.classList.add("cd-image-wrap--fallback");
  };

  const sections      = Array.isArray(course?.sections) ? course.sections : [];
  const lessonsCount  = Number(course?.lessons_count || 0);
  const reviewCount   = Number(course?.reviews_count || reviews.length || 0);
  const avgRating     = Number(course?.average_rating || 0);

  const courseFacts = [
    { key: "instructor", icon: UserRound,       label: isAr ? "المدرب"     : "Instructor", value: course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group") },
    { key: "level",      icon: TrendingUp,       label: isAr ? "المستوى"    : "Level",      value: course?.level || "Beginner" },
    { key: "duration",   icon: Clock3,           label: isAr ? "المدة"      : "Duration",   value: course?.duration_text || (isAr ? "مرنة" : "Flexible") },
    { key: "lessons",    icon: BookOpen,         label: isAr ? "الدروس"     : "Lessons",    value: String(lessonsCount) },
    { key: "sections",   icon: Layers3,          label: isAr ? "الأقسام"    : "Sections",   value: String(sections.length) },
    { key: "reviews",    icon: MessageSquareText,label: isAr ? "التقييمات"  : "Reviews",    value: String(reviewCount) },
  ];

  const handleRequireAuth = () => navigate("/login", { state: { from: location } });

  const handleEnroll = async () => {
    if (!isLogged) { handleRequireAuth(); return; }
    try {
      setActionLoading(true); setNotice(""); setError("");
      await enrollInCourse(course.id);
      navigate(`/learn/${courseUrlRef}`);
    } catch (err) {
      logger.error("Course enroll error", err);
      setError(err?.message || (isAr ? "تعذر التسجيل في الكورس." : "Unable to enroll in course."));
    } finally { setActionLoading(false); }
  };

  const handleCheckout = () => {
    if (!isLogged) { handleRequireAuth(); return; }
    navigate(`/courses/${courseUrlRef}/checkout`);
  };

  useEffect(() => {
    if (!isImageViewerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setIsImageViewerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isImageViewerOpen]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="cd-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="cd-loading-state">
          <div className="cd-loading-spinner" />
          <p>{isAr ? "جارِ تحميل الكورس..." : "Loading course..."}</p>
        </div>
      </div>
    );
  }

  if (error && !course) return <Navigate to="/courses" replace />;

  /* ── Action buttons ── */
  const renderActions = () => {
    if (enrollment) return (
      <>
        <Link to={`/learn/${courseUrlRef}`} className="cd-btn cd-btn--primary">
          <PlayCircle size={18} />{isAr ? "متابعة التعلم" : "Continue Learning"}
        </Link>
        <Link to="/my-courses" className="cd-btn cd-btn--ghost">
          {isAr ? "كورساتي" : "My Courses"}
        </Link>
      </>
    );
    if (course?.is_free) return (
      <button type="button" className="cd-btn cd-btn--primary" onClick={handleEnroll} disabled={actionLoading}>
        <Sparkles size={18} />
        {actionLoading ? (isAr ? "جارِ التسجيل..." : "Enrolling...") : (isAr ? "ابدأ مجانًا" : "Start for Free")}
      </button>
    );
    return (
      <button type="button" className="cd-btn cd-btn--primary" onClick={handleCheckout} disabled={actionLoading}>
        <ShoppingBag size={18} />
        {actionLoading ? (isAr ? "جارِ التحويل..." : "Redirecting...") : (isAr ? "اشترِ الآن" : "Buy Now")}
      </button>
    );
  };

  /* ── RENDER ── */
  return (
    <div className="cd-page" dir={isAr ? "rtl" : "ltr"}>

      {/* ══ HERO BANNER ══ */}
      <section className="cd-hero-banner">
        <div className="cd-hero-glow-1" />
        <div className="cd-hero-glow-2" />
        <div className="cd-hero-overlay" />

        <div className="cd-hero-inner">
          <Link to="/courses" className="cd-backlink">
            <ArrowLeft size={15} />
            {isAr ? "الرجوع إلى الكورسات" : "Back to Courses"}
          </Link>

          <div className="cd-topline">
            <span className="cd-pill">
              <FolderKanban size={13} />
              {course?.category || (isAr ? "كورس احترافي" : "Professional Course")}
            </span>
            <span className="cd-pill cd-pill--soft">
              <Award size={13} />
              {course?.level || "Beginner"}
            </span>
          </div>

          <h1 className="cd-hero-title">{title}</h1>

          <div className="cd-hero-meta">
            {avgRating > 0 && (
              <span className="cd-rating-chip">
                <Star size={13} fill="#ffb522" color="#ffb522" />
                <strong>{avgRating.toFixed(1)}</strong>
                <span>({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
              </span>
            )}
            <span className="cd-instructor-chip">
              <UserRound size={13} />
              {course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group")}
            </span>
          </div>

          {summary && <p className="cd-hero-summary">{summary}</p>}
        </div>
      </section>

      {/* ══ SHELL ══ */}
      <div className="cd-shell">

        {/* ── TOP GRID: Image (large) + Purchase Card (sticky) ── */}
        <div className="cd-top-grid">

          {/* Course image */}
          <div
            className={`cd-image-wrap${courseImage.isFallback ? " cd-image-wrap--fallback" : ""}`}
            role="button" tabIndex={0}
            onClick={() => setIsImageViewerOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsImageViewerOpen(true); } }}
            aria-label={isAr ? "عرض الصورة بالحجم الكامل" : "Open full image"}
          >
            <img
              src={courseImage.src} alt={title}
              className={`cd-image${courseImage.isFallback ? " cd-image--fallback" : ""}`}
              onError={handleImageError}
            />
            <span className="cd-image-hint">
              <Eye size={12} />{isAr ? "عرض كامل" : "Full view"}
            </span>
          </div>

          {/* Purchase card */}
          <aside className="cd-purchase-card">
            <div className="cd-purchase-card__eyebrow">
              <Sparkles size={13} />
              {isAr ? "التحاق مرن" : "Flexible enrollment"}
            </div>

            <div className="cd-purchase-card__price">
              <span className="cd-price-value">
                {course?.is_free ? (isAr ? "مجاني" : "Free") : formatCurrency(getCoursePrice(course))}
              </span>
            </div>

            {notice && <div className="cd-alert cd-alert--success">{notice}</div>}
            {error && course && <div className="cd-alert cd-alert--error">{error}</div>}

            <div className="cd-purchase-card__actions">{renderActions()}</div>

            <div className="cd-purchase-card__divider" />

            <div className="cd-trust-list">
              <div className="cd-trust-item"><ShieldCheck size={14} /><span>{isAr ? "وصول فوري بعد الشراء" : "Instant access after purchase"}</span></div>
              <div className="cd-trust-item"><CheckCircle2 size={14} /><span>{isAr ? "تنظيم واضح للدروس والتقدم" : "Clear lesson flow and progress"}</span></div>
              <div className="cd-trust-item"><ShoppingBag size={14} /><span>{isAr ? "مناسب للتعلم الذاتي العملي" : "Built for practical self-paced learning"}</span></div>
            </div>
          </aside>
        </div>

        {/* ── FACTS BAR (horizontal, full-width below image) ── */}
        <div className="cd-facts-bar">
          {courseFacts.map((fact) => {
            const Icon = fact.icon;
            return (
              <div key={fact.key} className="cd-facts-bar__item">
                <span className="cd-facts-bar__icon"><Icon size={15} /></span>
                <div className="cd-facts-bar__body">
                  <small>{fact.label}</small>
                  <strong>{fact.value}</strong>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── CONTENT AREA (single column, full-width) ── */}
        <div className="cd-content-area">

          {/* About */}
          {description && (
            <article className="cd-card">
              <div className="cd-card__head">
                <h2 className="cd-card__title">{isAr ? "عن الكورس" : "About this Course"}</h2>
              </div>
              <div
                className="cd-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            </article>
          )}

          {/* Learn + Requirements */}
          <div className="cd-dual-grid">
            <article className="cd-card">
              <div className="cd-card__head">
                <h3 className="cd-card__title">{isAr ? "ستتعلم" : "What You Will Learn"}</h3>
              </div>
              {learnPoints.length ? (
                <ul className="cd-bullet-list">
                  {learnPoints.map((item, idx) => (
                    <li key={`learn-${idx}`}><span className="cd-bullet-dot" />{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="cd-empty-text">{isAr ? "سيتم إضافة أهداف التعلم قريبًا." : "Learning outcomes will be added soon."}</p>
              )}
            </article>

            <article className="cd-card">
              <div className="cd-card__head">
                <h3 className="cd-card__title">{isAr ? "المتطلبات" : "Requirements"}</h3>
              </div>
              {requirements.length ? (
                <ul className="cd-bullet-list">
                  {requirements.map((item, idx) => (
                    <li key={`req-${idx}`}><span className="cd-bullet-dot" />{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="cd-empty-text">{isAr ? "لا توجد متطلبات خاصة لهذا الكورس." : "There are no special requirements for this course."}</p>
              )}
            </article>
          </div>

          {/* Curriculum */}
          {sections.length > 0 && (
            <article className="cd-card">
              <div className="cd-card__head">
                <h3 className="cd-card__title">{isAr ? "محتوى الكورس" : "Course Content"}</h3>
                <span className="cd-card__badge">
                  {sections.length} {isAr ? "قسم" : "sections"} · {lessonsCount} {isAr ? "درس" : "lessons"}
                </span>
              </div>
              <div className="cd-accordion">
                {sections.map((section) => {
                  const isOpen = openSectionId === section.id;
                  return (
                    <div key={section.id} className={`cd-accordion__item${isOpen ? " cd-accordion__item--open" : ""}`}>
                      <button
                        className="cd-accordion__trigger" type="button"
                        onClick={() => setOpenSectionId(isOpen ? null : section.id)}
                        aria-expanded={isOpen}
                      >
                        <span className="cd-accordion__icon"><Layers3 size={15} /></span>
                        <span className="cd-accordion__label">
                          {isAr ? section.title_ar || section.title_en : section.title_en || section.title_ar}
                        </span>
                        <span className="cd-accordion__count">
                          {(section.lessons || []).length} {isAr ? "درس" : "lessons"}
                        </span>
                        <ChevronDown size={15} className="cd-accordion__chevron" />
                      </button>
                      {isOpen && (
                        <div className="cd-accordion__body">
                          {(section.lessons || []).map((lesson) => (
                            <div key={lesson.id} className="cd-lesson-row">
                              <span className="cd-lesson-row__icon">
                                {lesson.is_preview ? <Eye size={13} /> : <Lock size={13} />}
                              </span>
                              <div className="cd-lesson-row__info">
                                <span className="cd-lesson-row__name">
                                  {isAr ? lesson.title_ar || lesson.title_en : lesson.title_en || lesson.title_ar}
                                </span>
                                <small className="cd-lesson-row__meta">
                                  {lesson.is_preview ? (isAr ? "معاينة متاحة" : "Preview available") : (isAr ? "للمشتركين فقط" : "Enrolled only")}
                                </small>
                              </div>
                              {lesson.is_preview && (
                                <span className="cd-badge cd-badge--preview">{isAr ? "معاينة" : "Preview"}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          )}

          {/* Who it's for + Reviews — bottom dual grid */}
          <div className="cd-dual-grid">
            <article className="cd-card cd-card--accent">
              <div className="cd-card__head">
                <h3 className="cd-card__title">{isAr ? "لمن هذا الكورس؟" : "Who Is This For?"}</h3>
              </div>
              <div className="cd-for-list">
                {[
                  isAr ? "المطور الذي يريد مساراً عملياً منظماً"               : "Developers who want a practical, structured path",
                  isAr ? "من يبحث عن تطبيقات واقعية وليس شرحاً سطحياً"        : "Learners who prefer real-world implementation",
                  isAr ? "من يريد تجربة تعلم حديثة وواضحة"                   : "Anyone looking for a clean, modern learning experience",
                ].map((text, i) => (
                  <div key={i} className="cd-for-item">
                    <CheckCircle2 size={14} className="cd-for-item__icon" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </article>

            {/* Reviews (shown in second col when few, else below) */}
            {reviews.length > 0 ? (
              <article className="cd-card">
                <div className="cd-card__head">
                  <h3 className="cd-card__title">{isAr ? "آراء الطلاب" : "Student Reviews"}</h3>
                  {avgRating > 0 && (
                    <span className="cd-rating-badge">
                      <Star size={12} fill="#ffb522" color="#ffb522" />{avgRating.toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="cd-reviews-list">
                  {reviews.slice(0, 4).map((review) => (
                    <div key={review.id} className="cd-review-card">
                      <div className="cd-reviewer">
                        <div className="cd-reviewer__avatar">
                          {String(review.user?.name || "S").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <strong className="cd-reviewer__name">{review.user?.name || (isAr ? "طالب" : "Student")}</strong>
                          <div className="cd-reviewer__stars">
                            <StarRating value={review.rating || 0} size={12} showValue={false} />
                          </div>
                        </div>
                      </div>
                      {review.comment && <p className="cd-review-comment">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              </article>
            ) : (
              /* Empty state placeholder keeps grid symmetric */
              <article className="cd-card cd-card--muted">
                <div className="cd-card__head">
                  <h3 className="cd-card__title">{isAr ? "التقييمات" : "Reviews"}</h3>
                </div>
                <div className="cd-empty-state">
                  <Star size={32} strokeWidth={1.5} />
                  <p>{isAr ? "لا توجد تقييمات حتى الآن." : "No reviews yet."}</p>
                  <small>{isAr ? "كن أول من يقيّم هذا الكورس!" : "Be the first to review this course!"}</small>
                </div>
              </article>
            )}
          </div>

        </div>{/* /cd-content-area */}
      </div>{/* /cd-shell */}

      {/* ── Lightbox ── */}
      {isImageViewerOpen && (
        <div
          className="cd-lightbox" role="dialog" aria-modal="true"
          aria-label={isAr ? "عرض صورة الكورس" : "Course image viewer"}
          onClick={() => setIsImageViewerOpen(false)}
        >
          <button type="button" className="cd-lightbox__close" onClick={() => setIsImageViewerOpen(false)} aria-label={isAr ? "إغلاق" : "Close"}>×</button>
          <img src={courseImage.src} alt={title} className="cd-lightbox__image" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
